import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { AlertTriangle, RefreshCw, WifiOff, Crown, Lock } from 'lucide-react';
import './PermissionGate.css';

/**
 * ErrorState Component
 * Displays error states with consistent styling matching PermissionGate
 * Handles:
 * 1. Suspended accounts - WhatsApp contact
 * 2. Expired/No subscription - Subscribe button
 * 3. Network errors - Retry button
 * 4. General errors - Retry button
 */
const ErrorState = ({ 
  error, 
  onRetry,
  className = '',
  children 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasActiveSubscription, accountSuspended, fetchPermissions } = usePermissions();
  
  // التحقق من حالة الحساب المعلق (suspended) - فقط من الـ context أو من user.isActive
  // لا نتحقق من محتوى رسالة الخطأ لأنها قد تسبب false positive
  const isAccountSuspended = accountSuspended || (user && user.isActive === false);

  // التحقق من انتهاء الاشتراك - فقط من hasActiveSubscription
  const isSubscriptionExpired = !hasActiveSubscription && !isAccountSuspended;

  // التحقق من خطأ الشبكة
  const isNetworkError = 
    error?.toLowerCase?.().includes('network') || 
    error?.toLowerCase?.().includes('الشبكة') ||
    error?.toLowerCase?.().includes('اتصال');

  // حالة الحساب المعلق - لها أولوية عليا
  if (isAccountSuspended) {
    return (
      <div className={`permission-denied ${className}`}>
        <div className="permission-denied-content">
          <div className="permission-denied-icon">
            <AlertTriangle size={48} />
          </div>
          <h2>الوصول مقيد</h2>
          <p>حسابك معلق حالياً</p>
          
          <div className="upgrade-prompt suspended-account">
            <AlertTriangle size={24} className="warning-icon" />
            <p className="suspended-message">
              <strong>حسابك معلق حالياً</strong>
            </p>
            <p>يرجى التواصل مع الدعم الفني لإعادة تفعيل حسابك</p>
            <a 
              href="https://wa.me/966544091800" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ marginTop: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              تواصل عبر الواتساب: +966 54 409 1800
            </a>
          </div>
        </div>
      </div>
    );
  }

  // حالة انتهاء الاشتراك أو عدم وجود اشتراك
  if (isSubscriptionExpired) {
    return (
      <div className={`permission-denied ${className}`}>
        <div className="permission-denied-content">
          <div className="permission-denied-icon" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>
            <Crown size={48} style={{ color: '#b45309' }} />
          </div>
          <h2>انتهى اشتراكك</h2>
          <p>يرجى تجديد اشتراكك للاستمرار في استخدام هذه الميزة</p>
          
          <div className="upgrade-prompt">
            <Crown size={24} className="crown-icon" />
            <p>أنت غير مشترك حالياً. اشترك للاستفادة من جميع الميزات</p>
            <div className="upgrade-buttons">
              <button 
                className="btn btn-primary upgrade-btn"
                onClick={() => navigate('/settings?tab=subscription')}
              >
                اشترك الآن
              </button>
              <button 
                className="btn btn-outline refresh-btn"
                onClick={() => {
                  fetchPermissions();
                  window.location.reload();
                }}
              >
                تحديث الصلاحيات
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // حالة خطأ الشبكة أو خطأ عام - فقط إذا كان هناك error
  if (!error) {
    return children || null;
  }

  return (
    <div className={`permission-denied ${className}`}>
      <div className="permission-denied-content">
        <div className="permission-denied-icon">
          {isNetworkError ? <WifiOff size={48} /> : <Lock size={48} />}
        </div>
        
        <h2>
          {isNetworkError ? 'خطأ في الاتصال' : 'حدث خطأ'}
        </h2>
        
        <div className="upgrade-prompt">
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            {isNetworkError 
              ? 'تعذر الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.'
              : error
            }
          </p>
          {onRetry && (
            <button 
              className="btn btn-primary"
              onClick={onRetry}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <RefreshCw size={18} />
              إعادة المحاولة
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorState;
